"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SubscriptionServiceStub = void 0;
/* eslint-disable @typescript-eslint/no-unused-vars */
const mystake_common_sdk_1 = require("@redbelly-tokeniser/mystake-common-sdk");
const ok = (payload) => ({ status: true, message: "ok", payload });
const stubSettings = () => ({
    allowSubscriptions: true,
    dealingFrequency: mystake_common_sdk_1.DealingFrequency.MONTHLY,
    cutoff: { weekday: 2, time: "14:00", timezone: "Australia/Sydney" },
    minimumSubscriptionUnits: 100,
    maximumSubscriptionUnits: 100000,
    roundingMethod: mystake_common_sdk_1.RoundingMethod.ROUND_DOWN,
});
// Completeness is derived, not asserted, so a stub-driven UI can reach the settings-incomplete state.
const settingsResponse = (settings) => ({
    settings,
    isComplete: (0, mystake_common_sdk_1.isSubscriptionSettingsComplete)(settings),
    missingFields: (0, mystake_common_sdk_1.getMissingSubscriptionSettingsFields)(settings),
    // The stub holds no applications, so nothing is locked; a stub-driven UI edits freely.
    lockedFields: [],
});
const STUB_SUBSCRIPTION_PRICE_CENTS = 100;
const STUB_COLLECTION_ACCOUNT = {
    accountName: "Melbourne Wholesale Income Fund",
    bsb: "062-123",
    accountNumber: "10203040",
};
const stubSubscription = (companyId, overrides = {}) => ({
    _id: "subscriptions-1",
    companyId,
    shareholderId: "shareholders-1",
    entityType: "individual",
    unitClassId: "shareclasses-1",
    amountAppliedCents: 800000,
    unitsIndicative: 8000,
    receivedAt: new Date("2026-09-01"),
    status: "applicationReceived",
    paymentStatus: "awaitingPayment",
    paymentReference: "CLA-8X42Q",
    events: [],
    createdAt: new Date("2026-09-01"),
    updatedAt: new Date("2026-09-01"),
    ...overrides,
});
const stubRow = (companyId, overrides = {}) => ({
    ...stubSubscription(companyId),
    investorName: "Jane Doe",
    unitClassName: "Class A",
    shareClassCode: "CLA",
    ...overrides,
});
// An approved member that is funded, so a stub-driven run screen has something to issue.
const stubRunRow = (companyId, overrides = {}) => ({
    ...stubRow(companyId),
    status: "approved",
    paymentStatus: "fundsReceived",
    unitsAllotted: 8000,
    subscriptionPriceCents: STUB_SUBSCRIPTION_PRICE_CENTS,
    considerationCents: 800000,
    ...overrides,
});
const stubRunGroup = (rows) => ({
    count: rows.length,
    units: rows.reduce((total, row) => total + (row.unitsAllotted ?? 0), 0),
    considerationCents: rows.reduce((total, row) => total + (row.considerationCents ?? 0), 0),
    rows,
});
const TAB_STATUSES = {
    all: ["applicationReceived", "rejected", "cancelled", "approvedInPrinciple", "approved", "unitsIssued"],
    open: ["applicationReceived", "approvedInPrinciple"],
    awaitingPayment: ["approved"],
    issued: ["unitsIssued"],
    outOfBand: ["approved", "unitsIssued"],
};
const onTab = (rows, filter) => rows.filter((row) => TAB_STATUSES[filter].includes(row.status) &&
    // Both predicates the real service applies, so a stub-driven screen shows what the API would.
    (filter !== "awaitingPayment" || row.paymentStatus !== "fundsReceived") &&
    (filter !== "outOfBand" || row.outOfBand === true));
const totalsFor = (rows) => ({
    count: rows.length,
    totalUnits: rows.reduce((total, row) => total + row.unitsIndicative, 0),
    totalAmountCents: rows.reduce((total, row) => total + row.amountAppliedCents, 0),
});
// Both sides of the screen come off the same filtered rows, so a stub-driven tab count can never
// disagree with the list under it.
const stubTotals = (rows) => Object.fromEntries(Object.keys(TAB_STATUSES).map((filter) => [filter, totalsFor(onTab(rows, filter))]));
class SubscriptionServiceStub {
    async getSubscriptionSettings(_companyId) {
        return ok(settingsResponse(stubSettings()));
    }
    async updateSubscriptionSettings(_companyId, settings) {
        return ok(settingsResponse({ ...stubSettings(), ...settings }));
    }
    async createSubscription(companyId, shareholderId, body) {
        const application = stubSubscription(companyId, {
            shareholderId,
            unitClassId: body.unitClassId,
            amountAppliedCents: Math.round(body.amountApplied * 100),
            unitsIndicative: Math.floor((body.amountApplied * 100) / STUB_SUBSCRIPTION_PRICE_CENTS),
        });
        return ok({
            ...application,
            dealingWindow: { dealingDate: new Date("2026-10-15"), closesAt: new Date("2026-10-14T04:00:00.000Z") },
            collectionAccount: STUB_COLLECTION_ACCOUNT,
        });
    }
    async recordSubscriptionVaultTransaction(_companyId, _shareholderId, _subscriptionId, _txHash) {
        // The stub has no vault, so it has no transaction to record.
        return { status: false, message: "The stub records no vault transactions.", payload: null };
    }
    async cancelSubscription(companyId, shareholderId, subscriptionId) {
        const application = stubSubscription(companyId, { _id: subscriptionId, shareholderId, status: "cancelled" });
        return ok({ ...application, events: [] });
    }
    async getShareholderSubscriptions(companyId, shareholderId) {
        return ok([{ ...stubRow(companyId, { shareholderId }), events: [], collectionAccount: STUB_COLLECTION_ACCOUNT }]);
    }
    async getSubscriptions(companyId, query = {}) {
        const rows = [stubRow(companyId)];
        return ok({ items: onTab(rows, query.filter ?? "all"), totals: stubTotals(rows) });
    }
    async getSubscriptionDetail(companyId, subscriptionId) {
        return ok({ ...stubRow(companyId, { _id: subscriptionId }), collectionAccount: STUB_COLLECTION_ACCOUNT });
    }
    async acceptSubscription(companyId, subscriptionId) {
        return ok(stubSubscription(companyId, {
            _id: subscriptionId,
            status: "approvedInPrinciple",
            dealingWindowKey: "2026-09-30:shareclasses-1",
        }));
    }
    async rejectSubscription(companyId, subscriptionId, body) {
        return ok(stubSubscription(companyId, { _id: subscriptionId, status: "rejected", outcomeNote: body.reason }));
    }
    async approveSubscription(companyId, subscriptionId) {
        return ok(stubSubscription(companyId, {
            _id: subscriptionId,
            status: "approved",
            unitsAllotted: 8000,
            subscriptionPriceCents: STUB_SUBSCRIPTION_PRICE_CENTS,
            considerationCents: 800000,
        }));
    }
    async issueSubscription(companyId, subscriptionId) {
        return ok(stubSubscription(companyId, { _id: subscriptionId, status: "unitsIssued", transactionId: "transactions-1" }));
    }
    async approveDealingWindow(companyId, windowKey) {
        return ok({
            windowKey,
            approvedCount: 1,
            skippedCount: 0,
            totalUnitsAllotted: 1000,
            totalConsiderationCents: 104000,
        });
    }
    async declareSubscriptionPayment(companyId, shareholderId, subscriptionId) {
        // The approval status stays where it was: a declaration moves the payment axis only.
        const application = stubSubscription(companyId, {
            _id: subscriptionId,
            shareholderId,
            paymentStatus: "paymentDeclared",
            paymentDeclaredAt: new Date("2026-09-02"),
        });
        return ok({ ...application, events: [], collectionAccount: STUB_COLLECTION_ACCOUNT });
    }
    async reconcileSubscriptionPayment(companyId, subscriptionId) {
        return ok(stubSubscription(companyId, {
            _id: subscriptionId,
            paymentStatus: "fundsReceived",
            paymentReconciledAt: new Date("2026-09-03"),
        }));
    }
    async getAllotmentRun(companyId, windowKey) {
        // One of each, so a stub-driven screen renders both groups and the held-back reason.
        return ok({
            windowKey,
            issuable: stubRunGroup([stubRunRow(companyId)]),
            heldBack: stubRunGroup([
                stubRunRow(companyId, {
                    _id: "subscriptions-2",
                    paymentStatus: "paymentDeclared",
                    missing: ["payment"],
                }),
            ]),
        });
    }
    async completeAllotmentRun(companyId, windowKey) {
        return ok({
            windowKey,
            issuedCount: 1,
            skippedCount: 0,
            heldBackCount: 1,
            totalUnitsIssued: 8000,
            totalConsiderationCents: 800000,
        });
    }
}
exports.SubscriptionServiceStub = SubscriptionServiceStub;
//# sourceMappingURL=SubscriptionServiceStub.js.map